import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useAbacatePay, createRafflePixPayment } from '../hooks/useAbacatePay';
import { abacatePayConfig } from '../lib/abacatepay-config';
import './ParticipationPage.css';

interface LocationState {
  selectedNumbers: number[];
  raffle: {
    id: string;
    title: string;
    spot_price: number;
    prize_description: string;
  };
}

export const ParticipationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { createPixPayment: createAbacatePayment, loading: abacateLoading } = useAbacatePay();
  
  const state = location.state as LocationState;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [participationId, setParticipationId] = useState<string | null>(null);
  const [pixCode, setPixCode] = useState<string | null>(null);
  const [qrCodeImage, setQrCodeImage] = useState<string | null>(null);
  const [paymentProof, setPaymentProof] = useState<File | null>(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(1800); // 30 minutes in seconds
  const [reservationExpired, setReservationExpired] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'pending' | 'paid' | 'expired' | 'error'>('pending');
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [customerTaxId, setCustomerTaxId] = useState<string>('');
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  
  // Check if AbacatePay is enabled
  const isAbacatePayEnabled = abacatePayConfig.isEnabled();

  useEffect(() => {
    if (!state || !state.selectedNumbers || state.selectedNumbers.length === 0) {
      navigate(`/raffle/${id}`);
      return;
    }

    if (!user) {
      // Redirect to login with return URL
      navigate('/login', { 
        state: { 
          from: `/raffle/${id}/participate`,
          selectedNumbers: state.selectedNumbers,
          raffle: state.raffle
        } 
      });
      return;
    }

    // Create reservation immediately
    createReservation();
  }, [user, state, id]);

  useEffect(() => {
    // Timer countdown
    if (participationId && !success && !reservationExpired) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setReservationExpired(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [participationId, success, reservationExpired]);

  const createReservation = async () => {
    if (!user || !state) return;

    try {
      setLoading(true);
      setError(null);

      // Check if user already has a participation in this raffle
      const { data: existingParticipation, error: checkError } = await supabase
        .from('participations')
        .select('*')
        .eq('raffle_id', id)
        .eq('user_id', user.id)
        .single();

      if (existingParticipation && existingParticipation.status !== 'cancelled') {
        setError('You already have a participation in this raffle');
        return;
      }

      // Create new participation (reservation)
      // Note: participations table expects different fields
      const { data: participation, error: participationError } = await supabase
        .from('participations')
        .insert({
          raffle_id: id,
          user_id: user.id,
          participant_name: user.user_metadata?.full_name || user.email || 'User',
          participant_phone: user.user_metadata?.phone || '00000000000',
          participant_email: user.email || '',
          spot_number: state.selectedNumbers.join(','), // Convert array to comma-separated string
          status: 'reserved',
          reserved_until: new Date(Date.now() + 30 * 60 * 1000).toISOString() // 30 minutes from now
        })
        .select()
        .single();

      if (participationError) {
        if (participationError.code === '23505') { // Unique constraint violation
          setError('You already have a participation in this raffle');
        } else {
          throw participationError;
        }
        return;
      }

      setParticipationId(participation.id);
      
      // Generate PIX payment
      if (isAbacatePayEnabled && user) {
        // Check if we need to get phone number and CPF
        if ((!user.user_metadata?.phone && !customerPhone) || (!user.user_metadata?.tax_id && !customerTaxId)) {
          setShowPhoneModal(true);
          return;
        }
        
        // Use AbacatePay for real PIX payment
        try {
          const abacatePayment = await createRafflePixPayment(
            participation.id,
            calculateTotal(),
            {
              name: user.user_metadata?.full_name || 'Cliente',
              email: user.email || '',
              phone: customerPhone || user.user_metadata?.phone || '00000000000',
              taxId: customerTaxId || user.user_metadata?.tax_id
            },
            {
              title: state.raffle.title,
              id: id || ''
            }
          );
          
          setPixCode(abacatePayment.pixCode);
          setQrCodeImage(abacatePayment.qrCodeImage || null);
          
          // Start polling for payment status
          startPaymentStatusPolling(abacatePayment.billingId);
          
        } catch (abacateError) {
          console.error('AbacatePay error, falling back to manual:', abacateError);
          // Fall back to manual PIX
          const manualPixCode = generatePixCode(participation.id, calculateTotal());
          setPixCode(manualPixCode);
        }
      } else {
        // Use manual PIX code generation
        const manualPixCode = generatePixCode(participation.id, calculateTotal());
        setPixCode(manualPixCode);
      }
    } catch (err: any) {
      console.error('Error creating reservation:', err);
      setError(err.message || 'Failed to create reservation');
    } finally {
      setLoading(false);
    }
  };

  const generatePixCode = (participationId: string, amount: number): string => {
    // This is a mock PIX code generator
    // In production, this would integrate with a real payment provider
    const pixKey = '00020126360014br.gov.bcb.pix0114+5511999999999';
    const txId = participationId.substring(0, 8);
    const amountStr = amount.toFixed(2).replace('.', '');
    
    return `${pixKey}5204000053039865402${amountStr}5802BR5909RifaTudo6009SAO PAULO62070503${txId}6304`;
  };

  const startPaymentStatusPolling = (billingId: string) => {
    // Clear any existing polling
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    // Poll every 5 seconds for payment status
    const interval = setInterval(async () => {
      try {
        const { data: payment, error } = await supabase
          .from('abacate_payments')
          .select('status')
          .eq('abacate_billing_id', billingId)
          .single();

        if (!error && payment) {
          if (payment.status === 'paid') {
            setPaymentStatus('paid');
            setSuccess(true);
            clearInterval(interval);
            
            // Update participation status
            if (participationId) {
              await supabase
                .from('participations')
                .update({ status: 'paid' })
                .eq('id', participationId);
            }
          } else if (payment.status === 'expired' || payment.status === 'cancelled') {
            setPaymentStatus(payment.status as 'expired');
            setReservationExpired(true);
            clearInterval(interval);
          }
        }
      } catch (error) {
        console.error('Error polling payment status:', error);
      }
    }, 5000); // Poll every 5 seconds

    setPollingInterval(interval);
  };

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setError('Invalid file type. Please upload JPG, PNG, or PDF');
        return;
      }
      
      setPaymentProof(file);
      setError(null);
    }
  };

  const handleUploadProof = async () => {
    if (!paymentProof || !participationId || !user) return;

    try {
      setUploadingProof(true);
      setError(null);

      // Upload file to Supabase Storage
      const fileName = `${user.id}/${participationId}/${Date.now()}-${paymentProof.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('payment-proofs')
        .upload(fileName, paymentProof);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('payment-proofs')
        .getPublicUrl(fileName);

      // Update participation with proof URL
      const { error: updateError } = await supabase
        .from('participations')
        .update({
          payment_proof_url: publicUrl,
          status: 'pending_payment' // Keep as pending, admin will approve
        })
        .eq('id', participationId);

      if (updateError) throw updateError;

      setSuccess(true);
    } catch (err: any) {
      console.error('Error uploading proof:', err);
      setError(err.message || 'Failed to upload payment proof');
    } finally {
      setUploadingProof(false);
    }
  };

  const handleCancelReservation = async () => {
    if (!participationId) return;

    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('participations')
        .update({ status: 'cancelled' })
        .eq('id', participationId);

      if (error) throw error;

      navigate(`/raffle/${id}`);
    } catch (err: any) {
      console.error('Error cancelling reservation:', err);
      setError(err.message || 'Failed to cancel reservation');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    if (!state) return 0;
    return state.selectedNumbers.length * state.raffle.spot_price;
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('PIX code copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="participation-loading">
        <div className="spinner"></div>
        <p>Creating your reservation...</p>
      </div>
    );
  }

  if (reservationExpired) {
    return (
      <div className="participation-expired">
        <h2>Reservation Expired</h2>
        <p>Your reservation time has expired. Please select your numbers again.</p>
        <button onClick={() => navigate(`/raffle/${id}`)}>Back to Raffle</button>
      </div>
    );
  }

  if (success) {
    return (
      <div className="participation-success">
        <div className="success-icon">✓</div>
        <h2>Payment Proof Uploaded!</h2>
        <p>Your payment proof has been submitted for review.</p>
        <p>You will receive a confirmation once your payment is validated.</p>
        <div className="success-details">
          <h3>Reservation Details</h3>
          <p><strong>Raffle:</strong> {state?.raffle.title}</p>
          <p><strong>Numbers:</strong> {state?.selectedNumbers.sort((a, b) => a - b).join(', ')}</p>
          <p><strong>Total:</strong> {formatCurrency(calculateTotal())}</p>
        </div>
        <button onClick={() => navigate('/dashboard')}>Go to Dashboard</button>
      </div>
    );
  }

  // Função para validar CPF
  const isValidCPF = (cpf: string): boolean => {
    // Remove caracteres não numéricos
    cpf = cpf.replace(/\D/g, '');
    
    // Verifica se tem 11 dígitos
    if (cpf.length !== 11) return false;
    
    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1{10}$/.test(cpf)) return false;
    
    // Valida dígitos verificadores
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cpf.charAt(i)) * (10 - i);
    }
    let digit = 11 - (sum % 11);
    if (digit > 9) digit = 0;
    if (parseInt(cpf.charAt(9)) !== digit) return false;
    
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cpf.charAt(i)) * (11 - i);
    }
    digit = 11 - (sum % 11);
    if (digit > 9) digit = 0;
    if (parseInt(cpf.charAt(10)) !== digit) return false;
    
    return true;
  };

  // Handle phone submission
  const handlePhoneSubmit = () => {
    const cleanPhone = customerPhone.replace(/\D/g, '');
    const cleanTaxId = customerTaxId.replace(/\D/g, '');
    
    if (cleanPhone.length < 10) {
      alert('Por favor, insira um telefone válido com DDD.');
      return;
    }
    
    if (cleanTaxId.length !== 11) {
      alert('Por favor, insira um CPF válido com 11 dígitos.');
      return;
    }
    
    // Validação de CPF
    if (!isValidCPF(cleanTaxId)) {
      alert('CPF inválido. Por favor, verifique os números digitados.');
      return;
    }
    
    setShowPhoneModal(false);
    // Re-run createReservation with phone and CPF
    createReservation();
  };

  return (
    <div className="participation-page">
      {/* Phone Modal */}
      {showPhoneModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">Informações para PIX</h2>
            <p className="text-gray-600 mb-4">Precisamos de alguns dados para gerar o PIX.</p>
            <div className="space-y-4">
              <div>
                <label htmlFor="phone" className="block text-sm font-medium mb-2">
                  Telefone (com DDD)
                </label>
                <input
                  id="phone"
                  type="tel"
                  placeholder="(11) 98765-4321"
                  value={customerPhone}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/\D/g, '');
                    let formatted = cleaned;
                    
                    if (cleaned.length >= 2) {
                      formatted = `(${cleaned.slice(0, 2)}) `;
                      if (cleaned.length > 2) {
                        formatted += cleaned.slice(2, 7);
                        if (cleaned.length > 7) {
                          formatted += `-${cleaned.slice(7, 11)}`;
                        }
                      }
                    }
                    
                    setCustomerPhone(formatted);
                  }}
                  maxLength={15}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              
              <div>
                <label htmlFor="taxId" className="block text-sm font-medium mb-2">
                  CPF
                </label>
                <input
                  id="taxId"
                  type="text"
                  placeholder="123.456.789-00"
                  value={customerTaxId}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/\D/g, '');
                    let formatted = cleaned;
                    
                    if (cleaned.length > 3) {
                      formatted = cleaned.slice(0, 3) + '.';
                      if (cleaned.length > 3) {
                        formatted += cleaned.slice(3, 6);
                        if (cleaned.length > 6) {
                          formatted += '.' + cleaned.slice(6, 9);
                          if (cleaned.length > 9) {
                            formatted += '-' + cleaned.slice(9, 11);
                          }
                        }
                      }
                    }
                    
                    setCustomerTaxId(formatted);
                  }}
                  maxLength={14}
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Estes dados são necessários para processar o pagamento PIX.
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowPhoneModal(false);
                    navigate(`/raffle/${id}`);
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handlePhoneSubmit}
                  disabled={customerPhone.replace(/\D/g, '').length < 10 || customerTaxId.replace(/\D/g, '').length !== 11}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Continuar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="participation-container">
        <div className="timer-bar">
          <span>Time Remaining: {formatTime(timeRemaining)}</span>
          <div className="timer-progress">
            <div 
              className="timer-fill" 
              style={{ width: `${(timeRemaining / 1800) * 100}%` }}
            ></div>
          </div>
        </div>

        <h1>Complete Your Participation</h1>
        
        <div className="participation-details">
          <h2>Selected Numbers</h2>
          <div className="selected-numbers">
            {state?.selectedNumbers.sort((a, b) => a - b).map(num => (
              <span key={num} className="number-badge">
                {num.toString().padStart(3, '0')}
              </span>
            ))}
          </div>
          
          <div className="payment-summary">
            <div className="summary-row">
              <span>Raffle:</span>
              <span>{state?.raffle.title}</span>
            </div>
            <div className="summary-row">
              <span>Quantity:</span>
              <span>{state?.selectedNumbers.length} numbers</span>
            </div>
            <div className="summary-row">
              <span>Price per number:</span>
              <span>{formatCurrency(state?.raffle.spot_price || 0)}</span>
            </div>
            <div className="summary-row total">
              <span>Total:</span>
              <span>{formatCurrency(calculateTotal())}</span>
            </div>
          </div>
        </div>

        <div className="payment-instructions">
          <h2>Payment Instructions</h2>
          
          {/* Show payment status indicator for AbacatePay */}
          {isAbacatePayEnabled && (
            <div className={`payment-status-indicator status-${paymentStatus}`}>
              {paymentStatus === 'pending' && '⏳ Aguardando pagamento...'}
              {paymentStatus === 'paid' && '✅ Pagamento confirmado!'}
              {paymentStatus === 'expired' && '⏰ Pagamento expirado'}
              {paymentStatus === 'error' && '❌ Erro no pagamento'}
            </div>
          )}
          
          <div className="pix-section">
            <h3>1. Make PIX Transfer</h3>
            <p>
              {isAbacatePayEnabled 
                ? 'Escaneie o QR Code ou copie o código PIX para fazer o pagamento:'
                : 'Copy the PIX code below and make the payment using your bank app:'}
            </p>
            
            {/* Show QR Code if available (AbacatePay) */}
            {qrCodeImage && (
              <div className="qr-code-container">
                <img 
                  src={qrCodeImage} 
                  alt="PIX QR Code" 
                  className="qr-code-image"
                  style={{ maxWidth: '300px', margin: '20px auto', display: 'block' }}
                />
              </div>
            )}
            
            {pixCode && (
              <div className="pix-code-container">
                <code className="pix-code">{pixCode}</code>
                <button 
                  className="copy-button"
                  onClick={() => copyToClipboard(pixCode)}
                >
                  Copy Code
                </button>
              </div>
            )}
            
            <div className="pix-info">
              <p><strong>Amount:</strong> {formatCurrency(calculateTotal())}</p>
              <p><strong>Recipient:</strong> Rifa Tudo</p>
            </div>
          </div>

          {/* Only show manual upload if AbacatePay is disabled */}
          {!isAbacatePayEnabled && (
            <div className="proof-section">
              <h3>2. Upload Payment Proof</h3>
              <p>After making the payment, upload the proof below:</p>
              
              <div className="file-upload">
                <input
                  type="file"
                  id="proof-upload"
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  disabled={uploadingProof}
                />
                <label htmlFor="proof-upload" className="file-label">
                  {paymentProof ? paymentProof.name : 'Choose file'}
                </label>
              </div>

              {error && (
                <div className="error-message">{error}</div>
              )}

              <div className="action-buttons">
                <button
                  className="upload-button"
                  onClick={handleUploadProof}
                  disabled={!paymentProof || uploadingProof}
                >
                  {uploadingProof ? 'Uploading...' : 'Submit Payment Proof'}
                </button>
                
                <button
                  className="cancel-button"
                  onClick={handleCancelReservation}
                  disabled={uploadingProof}
                >
                  Cancel Reservation
                </button>
              </div>
            </div>
          )}

          {/* Show automatic payment info for AbacatePay */}
          {isAbacatePayEnabled && (
            <div className="automatic-payment-info">
              <h3>💡 Pagamento Automático</h3>
              <p>
                Após realizar o pagamento PIX, a confirmação é automática! 
                Não é necessário enviar comprovante. O sistema atualizará o status em alguns segundos.
              </p>
              {paymentStatus === 'pending' && (
                <p className="checking-status">
                  🔄 Verificando status do pagamento...
                </p>
              )}
            </div>
          )}
        </div>

        <div className="participation-help">
          <h3>Need Help?</h3>
          <p>If you have any questions or issues with your payment, please contact our support team.</p>
          <p>Email: support@rifabr.com</p>
        </div>
      </div>
    </div>
  );
};